/**
 * BrandRegenBatchCard — Admin-Action für #281 "Batch-Regenerate bestehender
 * Rechnungs-PDFs mit neuem MietGo-Branding".
 *
 * Postet gegen /api/regenerate-branded-pdfs. Der Endpunkt ist hinter dem
 * Feature-Flag FEATURE_BRAND_REGEN_ENABLED eingezäunt — ohne gesetztes Flag
 * liefert der Server 403 und der Button zeigt die entsprechende Meldung.
 *
 * Ablauf:
 *   1. Admin gibt den Supabase-Service-Role-Key ein (einmalig, nicht gespeichert).
 *   2. Klick auf "Alle Mietwagen-Rechnungen neu brandieren".
 *   3. Bestätigungsdialog — Abbruch möglich.
 *   4. POST → Ergebnis (total/regenerated/skipped/failed + Fehlerliste).
 */

import { useState } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface RegenResult {
  total: number;
  regenerated: number;
  skipped: number;
  failed: number;
  errors: Array<{ id: string; akten_nr: string | null; error: string }>;
  target_version: string;
}

export default function BrandRegenBatchCard() {
  const [adminKey, setAdminKey] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RegenResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const run = async () => {
    setConfirmOpen(false);
    setRunning(true);
    setErrorMessage(null);
    setResult(null);
    try {
      const res = await fetch('/api/regenerate-branded-pdfs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey,
        },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const hint = body?.hint ? ` — ${body.hint}` : '';
        throw new Error(`${body?.error || `HTTP ${res.status}`}${hint}`);
      }
      setResult(body as RegenResult);
      toast.success(
        `Brand-Regen abgeschlossen: ${body.regenerated} neu, ${body.skipped} übersprungen, ${body.failed} Fehler`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(msg);
      toast.error(`Brand-Regen fehlgeschlagen: ${msg}`);
    } finally {
      setRunning(false);
    }
  };

  const canRun = adminKey.trim().length > 0 && !running;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-orange-500 to-rose-500" />
      <div className="px-5 py-4 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
            <RefreshCw className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-gray-900">Batch-Regenerate Mietwagen-Rechnungen</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Erzeugt alle bestehenden Mietwagen-Rechnungs-PDFs mit dem aktuellen MietGo-Briefbogen neu.
              Hinter dem Feature-Flag <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded">FEATURE_BRAND_REGEN_ENABLED</code> —
              ohne aktives Flag schlägt der Lauf fehl.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-100">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-800">
            Idempotent: Rechnungen mit aktueller Marken-Version werden übersprungen.
            Fehler einzelner Rechnungen unterbrechen den Lauf <strong>nicht</strong>.
          </p>
        </div>

        <div>
          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">
            Supabase Service-Role-Key
          </label>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="eyJhbGciOi..."
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-orange-200"
          />
          <p className="text-[10px] text-gray-400 mt-1">
            Einmalig für diesen Lauf — wird nicht gespeichert.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            disabled={!canRun}
            onClick={() => setConfirmOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#9B1C1C] text-white text-xs font-bold hover:bg-[#7F1D1D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Alle Mietwagen-Rechnungen neu brandieren
          </button>
          {running && <span className="text-[11px] text-gray-500">Läuft — kann bis zu 60 Sekunden dauern.</span>}
        </div>

        {errorMessage && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-red-800">Fehler</p>
              <p className="text-[11px] text-red-700 mt-0.5 whitespace-pre-wrap">{errorMessage}</p>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-2">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-[11px] text-emerald-900 space-y-0.5">
                <p className="font-semibold">Lauf abgeschlossen · Version <code className="bg-emerald-100 px-1 rounded">{result.target_version}</code></p>
                <p>{result.total} Forderungsakten geprüft</p>
                <p>{result.regenerated} neu erzeugt · {result.skipped} übersprungen · {result.failed} Fehler</p>
              </div>
            </div>
            {result.errors.length > 0 && (
              <details className="bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                <summary className="text-[11px] font-semibold text-red-800 cursor-pointer">
                  {result.errors.length} Fehler anzeigen
                </summary>
                <ul className="mt-2 space-y-1">
                  {result.errors.map((e) => (
                    <li key={e.id} className="text-[11px] text-red-700">
                      <code className="font-mono">{e.akten_nr || e.id}</code> — {e.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">Wirklich alle Mietwagen-Rechnungen neu brandieren?</h3>
                <p className="text-[11px] text-gray-500 mt-1">
                  Der Job iteriert über alle <code>forderungsakten</code> mit Rechnungsnummer und
                  erzeugt die PDFs mit dem aktuellen MietGo-Briefbogen neu. Bereits gebrandete
                  Rechnungen werden übersprungen. Der Lauf kann bis zu 60 Sekunden dauern.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100"
              >
                Abbrechen
              </button>
              <button
                onClick={run}
                className="px-3 py-1.5 rounded-lg bg-[#9B1C1C] text-white text-xs font-bold hover:bg-[#7F1D1D]"
              >
                Jetzt starten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
